import { View, Text } from 'react-native'
import { Progress } from 'tamagui'
import { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { formatCurrency } from '@/src/common/utils/general'
import { z } from 'zod/v4'
import { mesaColorNames, typography } from '@/src/common/theme'
import { useBenefitStatementCredits } from '../hooks/useBenefitStatementCredits'

const REFRESHES = ['MONTHLY', 'QUARTERLY', 'ANNUALLY', 'null'] as const

const Refresh = z.enum(REFRESHES)

type Refresh = z.infer<typeof Refresh>

const REFRESHES_MULTIPLIER = {
  MONTHLY: 12,
  QUARTERLY: 4,
  ANNUALLY: 1,
  null: 1,
} as const satisfies Record<Refresh, number>

const CURRENT_YEAR = DateTime.now().year

const REFRESHES_START_END = {
  MONTHLY: Array(12)
    .fill(undefined)
    .map((_, i) => {
      const start = DateTime.fromObject({ year: CURRENT_YEAR, month: i + 1, day: 1 })
      const end = start.endOf('month')
      return { start: start.toISODate(), end: end.toISODate() } as const
    }),
  QUARTERLY: [1, 2, 3, 4].map((q) => {
    const startMonth = (q - 1) * 3 + 1
    const start = DateTime.fromObject({ year: CURRENT_YEAR, month: startMonth, day: 1 })
    const end = start.plus({ months: 3 }).minus({ days: 1 })
    return { start: start.toISODate(), end: end.toISODate() } as const
  }),
  ANNUALLY: [1].map((_) => {
    const start = DateTime.fromObject({ year: CURRENT_YEAR, month: 1, day: 1 })
    const end = start.plus({ months: 12 }).minus({ days: 1 })
    return { start: start.toISODate(), end: end.toISODate() } as const
  }),
  null: [{ start: null, end: null }] as const,
} as const satisfies Record<Refresh, { start: string | null; end: string | null }[]>

const EnrolledBadge = () => (
  <View
    style={{
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'center',
      backgroundColor: mesaColorNames['semantic/bg/success rgba(232, 250, 240, 1)'],
      borderRadius: 10,
      paddingHorizontal: 10,
      paddingVertical: 4,
    }}
  >
    <Text
      style={[
        typography['XS Bold'],
        { color: mesaColorNames['semantic/fg/success #008557'], fontSize: 12 },
      ]}
    >
      Enrolled
    </Text>
  </View>
)

export const BenefitRedemptionProgress = ({
  benefitId,
  isEnrolled,
  creditPerRefresh,
  refreshes,
  creditRedemption,
}: {
  benefitId: string
  isEnrolled: boolean
  creditPerRefresh: number | null
  refreshes: string | null
  creditRedemption: string | null
}) => {
  const [redemptionAmount, setRedemptionAmount] = useState(0)

  const { data: benefitStatementCreditsData } = useBenefitStatementCredits({ benefitId })

  useEffect(() => {
    if (benefitStatementCreditsData) {
      setRedemptionAmount(benefitStatementCreditsData)
    }
  }, [benefitId, benefitStatementCreditsData])

  if (isEnrolled && creditRedemption === 'GIFT_CARD') {
    return (
      <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
        <EnrolledBadge />
      </View>
    )
  }

  if (creditPerRefresh && isEnrolled) {
    const parsedRefreshes = Refresh.parse(`${refreshes}`)

    const benefitCreditIssued = redemptionAmount
    const totalBenefitRedemption = creditPerRefresh * REFRESHES_MULTIPLIER[parsedRefreshes]
    const benefitCreditClaimed = Math.min((benefitCreditIssued / totalBenefitRedemption) * 100, 100)

    const today = DateTime.now()
    const periods = REFRESHES_START_END[parsedRefreshes]
    const daysToRefresh = periods
      .values()
      .flatMap((period) =>
        period.start &&
        period.end &&
        today >= DateTime.fromISO(period.start) &&
        today <= DateTime.fromISO(period.end)
          ? ([period.end] as const)
          : ([] as const),
      )
      .map((end) => today.diff(DateTime.fromISO(end), 'days').toObject().days)
      .take(1)
      .next().value

    return (
      <>
        <Progress
          key={benefitId}
          value={benefitCreditClaimed}
          height={8}
          style={{ backgroundColor: mesaColorNames['global/grey/20 #E6E9EB'] }}
        >
          <Progress.Indicator
            animation="bouncy"
            style={{ backgroundColor: mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9'] }}
          />
        </Progress>

        <View style={{ flex: 1 }}>
          <Text
            style={[
              typography.XS,
              {
                color:
                  redemptionAmount === 0
                    ? mesaColorNames['Greyscale/600 #505D64']
                    : mesaColorNames['Mesa Primary Scale/500 #00C7B6'],
                marginTop: 8,
              },
            ]}
            numberOfLines={4}
          >
            {`${formatCurrency(benefitCreditIssued.toString(), {
              hideCurrencySymbol: false,
              decimals: 0,
            })} ${creditRedemption === 'BONUS_POINTS' ? 'Spent' : 'Earned'}`}
          </Text>
        </View>

        <View style={{ flexDirection: 'row' }}>
          <View style={{ flex: 1, justifyContent: 'center' }}>
            {daysToRefresh && (
              <Text
                style={[typography.XS, { color: mesaColorNames['Greyscale/600 #505D64'] }]}
                numberOfLines={4}
              >
                {`Refreshes in ${Math.round(Math.abs(daysToRefresh))} days`}
              </Text>
            )}
          </View>

          <EnrolledBadge />
        </View>
      </>
    )
  } else {
    return <></>
  }
}
